import { env } from '@/lib/env';

/**
 * Sentry and PostHog, both lazy and both no-ops when their env vars are
 * empty. Neither SDK is in the entry chunk: they load after first paint, so
 * the sign-in route's budget only pays for this file. Their masks (./mask)
 * load with them, before either starts, so nothing is ever sent unmasked.
 *
 * What is asked of them before they load (who she is, the first pages she
 * sees) is held here and handed over once they start: the first page of a
 * visit used to go uncounted and unidentified, because the router reports it
 * before the SDKs have loaded.
 *
 * Sentry traces a share of page loads and navigations (TRACE_RATE), and its
 * `sentry-trace` header goes to the API only (`tracePropagationTargets`), so
 * a kept page's trace runs on through dreamwell-api (which keeps its half:
 * infrastructure/observability/sentryPolicy.ts).
 */

import type * as SentryModule from '@sentry/react';
import type { PostHog } from 'posthog-js';
import type * as MaskModule from './mask';

/** The share of page loads and navigations traced in production. */
export const TRACE_RATE = 0.2;
/** Pages held for PostHog before it starts, at most. */
const HELD_PAGES = 20;

type Identity = { user: { id: string; email?: string | null; name?: string | null }; team?: { id: number; name: string } } | null;

let sentry: typeof SentryModule | null = null;
let posthog: PostHog | null = null;
let masks: typeof MaskModule | null = null;
/** Who she is, as last said: undefined until said; null once reset. */
let identity: Identity | undefined;
let heldPages: string[] = [];

const loadMasks = () =>
  import('./mask').then((mod) => {
    masks = mod;
    return mod;
  });

/** The router Sentry names page loads and navigations by (its route patterns, never an id). */
type TracedRouter = Parameters<typeof SentryModule.tanstackRouterBrowserTracingIntegration>[0];

export async function initObservability(router?: TracedRouter): Promise<void> {
  const tasks: Promise<unknown>[] = [];
  if (env.sentryDsn) {
    tasks.push(
      Promise.all([import('@sentry/react'), loadMasks()]).then(([mod, mask]) => {
        mod.init({
          dsn: env.sentryDsn,
          environment: env.isProd ? 'production' : 'development',
          integrations: router ? [mod.tanstackRouterBrowserTracingIntegration(router)] : [],
          tracesSampleRate: env.isProd ? TRACE_RATE : 0,
          tracePropagationTargets: [env.backendUrl],
          // An agency's upload link carries its key in the address: no breadcrumb keeps it as it is recorded (a logged
          // API error's object included), and no error report sends it.
          beforeBreadcrumb: mask.maskSentryBreadcrumb,
          beforeSend: mask.maskSentryEvent,
          beforeSendTransaction: mask.maskSentryEvent,
        });
        sentry = mod;
      }),
    );
  }
  if (env.posthogToken) {
    tasks.push(
      Promise.all([import('posthog-js'), loadMasks()]).then(([{ default: ph }, mask]) => {
        ph.init(env.posthogToken, {
          api_host: env.posthogHost || undefined,
          capture_pageview: false,
          persistence: 'localStorage+cookie',
          // An agency's upload link carries its key in the address: no event (a click's included) may send it.
          before_send: mask.maskCaptured,
        });
        posthog = ph;
      }),
    );
  }
  await Promise.allSettled(tasks);
  if (identity !== undefined) applyIdentity(identity);
  const pages = heldPages;
  heldPages = [];
  for (const path of pages) trackPageview(path);
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!env.isProd) console.error('[captured]', error, context);
  sentry?.captureException(error, context ? { extra: context } : undefined);
}

function applyIdentity(who: Identity): void {
  if (!who) {
    sentry?.setUser(null);
    sentry?.setTag('team_id', undefined);
    posthog?.reset();
    return;
  }
  const { user, team } = who;
  sentry?.setUser({ id: user.id, email: user.email ?? undefined });
  if (team) sentry?.setTag('team_id', String(team.id));
  if (posthog) {
    posthog.identify(user.id, { email: user.email, name: user.name });
    if (team) posthog.group('team', String(team.id), { name: team.name });
  }
}

export function identify(user: { id: string; email?: string | null; name?: string | null }, team?: { id: number; name: string }): void {
  identity = { user, team };
  applyIdentity(identity);
}

export function resetIdentity(): void {
  identity = null;
  applyIdentity(null);
}

/**
 * A page seen, its path as the router has it. An agency's upload link is named `/upload/:token` here: its token never
 * reaches analytics. PostHog starts only once the masks have loaded, so they are always here when it is; a page seen
 * before then is held and sent when it starts.
 */
export function trackPageview(path: string): void {
  if (posthog && masks) posthog.capture('$pageview', { $current_url: window.location.origin + masks.maskPath(path) });
  else if (env.posthogToken && heldPages.length < HELD_PAGES) heldPages.push(path);
}

/**
 * The route pattern of an API path: ids, long tokens and query strings taken out, so one action is one name
 * ("/email/v2/threads/:id/reply").
 */
export function routePattern(endpoint: string): string {
  const path = endpoint.split('?')[0]!.split('#')[0]!;
  return (
    path
      .split('/')
      .map((part) => (/^\d+$/.test(part) || /^[0-9a-f-]{16,}$/i.test(part) || (/^[A-Za-z0-9_-]{20,}$/.test(part) && /\d/.test(part)) ? ':id' : part))
      .join('/') || '/'
  );
}

/**
 * A write that went through (`api` client), as PostHog's `api_write`: its method and route pattern (an upload link's
 * key masked), so a funnel can be built on any action she takes. Sent only once PostHog has started.
 */
export function trackWrite(method: string, endpoint: string): void {
  if (!posthog || !masks) return;
  const pattern = masks.maskPath(routePattern(endpoint));
  posthog.capture('api_write', { method, route: `${method} ${pattern}`, path: pattern });
}

export function track(event: string, props?: Record<string, unknown>): void {
  posthog?.capture(event, props);
}
